// src/components/SupportTicketDialog.tsx
import * as React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Button,
  Divider,
  Box,Snackbar, Alert
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import {
  SupportTicketForm,
  RoleId,
  MenuId,
  CategoryId,
  FunctionId,
} from "../../types/ticket";
import {
  getInitialFormState,
  resetOnMenuChange,
  resetOnCategoryChange,
  resetOnFunctionChange,
} from "./SupportTicketFormState";
import { useTicketOptions } from "../../hooks/useTicketOptions";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import {
  setSelectedRow,
  setSelectedDropdownValue,
  setIsSelectionRemove,
} from "../../redux/actions/supportTicket";
import { RootState } from "@/redux/store";
import { useSnackbar } from "../SnakBar/AppSnackbarProvider";
import { createSupportTicket } from "../../api/supportTicket";
import { getPlantsApi } from "../../api/getAdminApi";
import ErrorDialog from "../common/ErrorDialog/ErrorDialog";
import { hasRolePermission } from "../../constants/roleAccess";

type SelectedRowItem = {
  materialPlant?: number | string;
  extractionDate?: string;
  materialNumber?: string | number;
  [key: string]: any;
};

const isMeaningfulValue = (value: unknown) => {
  if (value === null || value === undefined) return false;
  if (typeof value === "string") return value.trim() !== "";
  return true;
};

const getFirstAvailableValue = (
  row: Record<string, any>,
  keys: string[],
) => {
  for (const key of keys) {
    const value = row?.[key];
    if (isMeaningfulValue(value)) return value;
  }
  return "";
};

const TECHNICAL_KEYS = new Set([
  "id",
  "__uid",
  "__typename",
  "_id",
  "isNew",
]);

const formatFieldLabel = (key: string) => {
  return String(key)
    .replace(/[_-]+/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/^./, (s) => s.toUpperCase());
};

type Props = {
  open: boolean;
  setOpen: (open: boolean) => void;
  role?: RoleId; // role of logged-in user
};

export default function SupportTicketDialog({
  open,
  setOpen,
  role = "planner",
}: Props) {
  const [form, setForm] = React.useState<SupportTicketForm>(() => getInitialFormState());
  const dropdownPresetRef = React.useRef(false); // Track if dropdowns have been preset
  const dispatch = useDispatch();
  const [loading , setLoading] = React.useState(false);
  const {
    menuOptions,
    categoryOptions,
    functionOptions,
    isCategoryDisabled,
    isFunctionDisabled,
  } = useTicketOptions(form, role);
 const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  // Pull selected rows from your Redux slice
  const { selectedRow, selectedDropdownValue } = useSelector(
    (state: any) => state.supportTicket,
  );
    const currentRole = useSelector(
      (state: any) => state?.userRoles?.currentRole || null,
    );
    const selectedRole = currentRole;
  const canCreateSupportTicket = hasRolePermission(
    selectedRole,
    "create_support_ticket",
  );
  // Plant dropdown state
  const [plantOptions, setPlantOptions] = React.useState<string[]>([]);
  const [locationData, setLocationData] = React.useState<string>("");
  const storedPlant = sessionStorage.getItem("selectedPlant");
  const location = useLocation();
  const hidePlantDropdown = ["/admin/data-error", "/admin/support-ticket-summary","/admin/adminView"].includes(location.pathname);
  // Fetch plant options on mount
  React.useEffect(() => {
  setPlantOptions(JSON.parse(storedPlant || "[]"));
    if (storedPlant) {
      try {
        const parsed = JSON.parse(storedPlant);
        if (Array.isArray(parsed) && parsed.length > 0) {
          setLocationData(parsed[0]);
          setForm((prev) => ({ ...prev, plantid: parsed[0] }));
        }
      } catch {}
    }
  }, [storedPlant]);

  // Refresh initiator every time the dialog opens, in case userId
  // wasn't in storage yet when this component first mounted.
  React.useEffect(() => {
    if (!open) return;
    const userId =
      (localStorage.getItem("userId") || sessionStorage.getItem("userId") || "").trim();
    if (userId) {
      setForm((prev) => (prev.initiator === userId ? prev : { ...prev, initiator: userId }));
    }
  }, [open]);

  // Handle plant dropdown change
  const handlePlantChange = (e: any) => {
    const value = e.target.value;
    setLocationData(value);
    setForm((prev) => ({ ...prev, plantid: value }));
    
  };
  const [snackbarOpen, setSnackbarOpen] = React.useState(false);
const [snackbarMessage, setSnackbarMessage] = React.useState("");
const [snackbarSeverity, setSnackbarSeverity] = React.useState<"success" | "error" | "info" | "warning">("info");
  const pillInput = {
    "& .MuiOutlinedInput-input": {
      background: "#f2f4f5",
      borderRadius: "20px",
      padding: "6px 12px",
    },
  } as const;

  const handleChange = (key: keyof SupportTicketForm) => (e: any) => {
    const isCheckbox = e.target?.type === "checkbox";
    const value = isCheckbox ? e.target.checked : e.target.value;

    setForm((prev) => {
      if (key === "menu") return resetOnMenuChange(prev, value);
      if (key === "category") return resetOnCategoryChange(prev, value);
      if (key === "func") return resetOnFunctionChange(prev, value);
      return { ...prev, [key]: value } as SupportTicketForm;
    });
  };

  const onClose = () => {
    setLoading(false)
    dropdownPresetRef.current = false; // Reset preset flag
    setForm(getInitialFormState());
    setOpen(false);
    dispatch(setIsSelectionRemove(true));
    dispatch(setSelectedRow([]));
    dispatch(setSelectedDropdownValue(null));
  };

 const handleSubmit = async () => {
  if (!canCreateSupportTicket) return;
  try {
    setLoading(true);
    console.log("selectedRow",selectedRow)
    const updateSelectedRow = selectedRow?.map((item: Record<string, any>) => {
      return {
        materialNumber: getFirstAvailableValue(item, [
          "materialNumber",
          "Material Number",
          "material",
          "Material",
        ]),
        materialPlant: getFirstAvailableValue(item, [
          "materialPlant",
          "Plant",
          "plant",
          "plantId",
          "Plant Id",
        ]),
        ioExtractionDate: getFirstAvailableValue(item, [
          "Extraction Date",
          "extractionDate",
          "ioExtractionDate",
        ]),
      };
    });
    const payload: any = { ...form, function: form.func, plantid: hidePlantDropdown || hasSelectedRows ? null : locationData, 
      selectedRow: updateSelectedRow };
    const response = await createSupportTicket(payload);
    setLoading(false);
   

    // SUCCESS snackbar
    setSnackbarMessage("Support Ticket Created Successfully");
    setSnackbarSeverity("success");
    setSnackbarOpen(true);
 onClose();
  } catch (e) {
    // ERROR snackbar
    setSnackbarMessage("Something Went Wrong");
    setSnackbarSeverity("error");
    setSnackbarOpen(true);
    setLoading(false);
  } finally {
    setLoading(false);
    // NOTE: do not close snackbar here
  }
};


  // ----- Submit enablement logic (with dropdown-empty guard) -----
const baseChecks =
  !!form.menu &&
  !!form.category &&
  !!form.func &&
  !!form.description?.trim();

const hasSelectedRows = Array.isArray(selectedRow) && selectedRow.length > 0;

const canSubmit = canCreateSupportTicket && baseChecks;
// -----------------------------------------------------

  

  // ----- Auto-preset Menu/Category/Function when selected rows exist (NOT LOCKED) -----
  const findIdByLabel = (
    value: string | undefined,
    options: { id: string; label: string }[],
  ) => {
    if (!value) return "";
    const normalized = value.toLowerCase();
    const match = options.find(
      (o) =>
        o.label.toLowerCase() === normalized ||
        o.id.toLowerCase() === normalized,
    );
    return match?.id ?? "";
  };

  React.useEffect(() => {
    const hasSelected = Array.isArray(selectedRow) && selectedRow.length > 0;

    // If no rows selected, reset the preset flag for next selection
    if (!hasSelected) {
      dropdownPresetRef.current = false;
      return;
    }

    // Only preset dropdown values once per selection
    if (dropdownPresetRef.current) return;

    // Resolve IDs by labels so we don't hard-code IDs
    const menuId = findIdByLabel(selectedDropdownValue?.menuId, menuOptions);
    const categoryId = findIdByLabel(
      selectedDropdownValue?.categoryId,
      categoryOptions,
    );
    const functionId = findIdByLabel(
      selectedDropdownValue?.functionId,
      functionOptions,
    );

    setForm((prev) => ({
      ...prev,
      menu: menuId ? (menuId as MenuId) : prev.menu,
      category: categoryId ? (categoryId as CategoryId) : prev.category,
      func: functionId ? (functionId as FunctionId) : prev.func,
    }));

    dropdownPresetRef.current = true; // Mark as preset
  }, [
    selectedRow,
    selectedDropdownValue,
    menuOptions,
    categoryOptions,
    functionOptions,
  ]);
  console.log("menuOptions",menuOptions)
  return (
  <>
<ErrorDialog
  open={snackbarOpen && snackbarSeverity === "error"}
  message={snackbarMessage}
  onClose={() => setSnackbarOpen(false)}
/>
<Snackbar
  open={snackbarOpen && snackbarSeverity !== "error"}
  autoHideDuration={2500}
  onClose={() => setSnackbarOpen(false)}
  anchorOrigin={{ vertical: "top", horizontal: "center" }}
>
  <Alert
    onClose={() => setSnackbarOpen(false)}
    severity={snackbarSeverity}
    sx={{ width: "100%" }}
    variant="filled"
  >
    {snackbarMessage}
  </Alert>
</Snackbar>
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
    <DialogTitle sx={{ fontSize: 20, fontWeight: 600, position: 'relative', pr: 12 }}>
  Support Ticket

  {/* Top-right: Material Number pill + Close icon */}
  <Box
    sx={{
      position: 'absolute',
      right: 8,
      top: 8,
      display: 'flex',
      alignItems: 'center',
      gap: 1.5,
      maxWidth: { xs: '55%', md: '60%' },
    }}
  >
    {/* <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.75,
        px: 1,
        py: 0.5,
        borderRadius: 1.5,
        backgroundColor: '#f5f5f5',
        color: 'text.primary',
        maxWidth: '100%',
      }}
      title={String(locationData)} // tooltip for long values
    >
      <span style={{ fontSize: 12, opacity: 0.75 }}>Material Plant:</span>
      <span
        style={{
          fontWeight: 600,
          fontSize: 13,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          maxWidth: '18ch',
        }}
      >
        {locationData}
      </span>
    </Box> */}

    <IconButton
      onClick={onClose}
      aria-label="close"
      size="small"
      sx={{ ml: 0.5 }}
    >
      <CloseIcon />
    </IconButton>
  </Box>
</DialogTitle>

      <Divider />

      <DialogContent dividers sx={{ mt: 2 }}>
        {/* Row 1: 4 dropdowns — kept editable */}
       
        <Box
          sx={{
            display: "grid",
            gap: 2,
            gridTemplateColumns: { xs: "1fr", md: hidePlantDropdown || hasSelectedRows ? "repeat(4, 1fr)" : "repeat(5, 1fr)" },
          }}
        >
          {/* Plant Dropdown */}
          {!hidePlantDropdown && !hasSelectedRows && (
            <FormControl size="small">
              <InputLabel>Plant</InputLabel>
              <Select
                label="Plant"
                value={locationData}
                onChange={handlePlantChange}
              >
                {plantOptions.map((plant) => (
                  <MenuItem key={plant} value={plant}>
                    {plant}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          {/* Menu (role-filtered) — NOT locked */}
          <FormControl size="small">
            <InputLabel>Menu</InputLabel>
            <Select
              label="Menu"
              value={form.menu}
              onChange={handleChange("menu")}
            >
              {menuOptions.map((opt) => (
                <MenuItem key={opt.id} value={opt.id}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Category — respect role gating only, NOT locked by selection */}
          <FormControl size="small" disabled={isCategoryDisabled}>
            <InputLabel>Category</InputLabel>
            <Select
              label="Category"
              value={form.category}
              onChange={handleChange("category")}
            >
              {categoryOptions.map((opt) => (
                <MenuItem key={opt.id} value={opt.id}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Function — respect role gating only, NOT locked by selection */}
          <FormControl size="small" disabled={isFunctionDisabled}>
            <InputLabel>Function</InputLabel>
            <Select
              label="Function"
              value={form.func}
              onChange={handleChange("func")}
            >
              {functionOptions.map((opt) => (
                <MenuItem key={opt.id} value={opt.id}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

        </Box>

        {/* Selected rows preview (scrollable) */}
        {Array.isArray(selectedRow) && selectedRow.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <Box
              sx={{
                maxHeight: 260, // visible height before scroll
                overflowY: "auto", // internal scroll
                border: "1px solid #e0e0e0",
                borderRadius: 2,
                p: 2,
                backgroundColor: "#fafafa",
              }}
            >
              {Array.isArray(selectedRow) && selectedRow.length > 0
                ? (selectedRow as SelectedRowItem[]).map((item, idx) => (
                    <Box
                      key={(item.materialNumber as any) ?? (item.id as any) ?? idx}
                      sx={{
                        display: "grid",
                        gridTemplateColumns: {
                          xs: "1fr",
                          md: "repeat(3, 1fr)",
                        },
                        gap: 2,
                        mb: 2,
                      }}
                    >
                      {(() => {
                        const primaryFields = [
                          {
                            label: "Extraction Date",
                            value: getFirstAvailableValue(item, [
                              "Extraction Date",
                              "extractionDate",
                              "ioExtractionDate",
                            ]),
                          },
                          {
                            label: "Material Number",
                            value: getFirstAvailableValue(item, [
                              "materialNumber",
                              "Material Number",
                            ]),
                          },
                          {
                            label: "Plant",
                            value: getFirstAvailableValue(item, [
                              "materialPlant",
                              "Plant",
                              "plant",
                            ]),
                          },
                        ].filter((field) => isMeaningfulValue(field.value));

                        const extraFields = Object.entries(item)
                          .filter(([key, value]) => {
                            if (TECHNICAL_KEYS.has(key)) return false;
                            if (!isMeaningfulValue(value)) return false;
                            return ![
                              "Extraction Date",
                              "extractionDate",
                              "ioExtractionDate",
                              "materialNumber",
                              "Material Number",
                              "materialPlant",
                              "Plant",
                              "plant",
                            ].includes(key);
                          })
                          .slice(0, 3)
                          .map(([key, value]) => ({
                            label: formatFieldLabel(key),
                            value,
                          }));

                        const fieldsToRender =
                          primaryFields.length > 0
                            ? primaryFields
                            : extraFields;

                        return fieldsToRender.length > 0 ? (
                          fieldsToRender.map((field) => (
                            <TextField
                              key={`${field.label}-${idx}`}
                              size="small"
                              label={field.label}
                              value={String(field.value ?? "")}
                              disabled
                              sx={pillInput}
                            />
                          ))
                        ) : (
                          <TextField
                            size="small"
                            label="Selected Row"
                            value="No displayable values"
                            disabled
                            sx={pillInput}
                          />
                        );
                      })()}
                    </Box>
                  ))
                : null}
            </Box>
          </Box>
        )}

        {/* Description */}
        <Box sx={{ mt: 2 }}>
          <TextField
            fullWidth
            multiline
            minRows={5}
            label="Issue Description"
            value={form.description}
            onChange={handleChange("description")}
          />
        </Box>
      </DialogContent>

      {/* Actions: Left = Initiator + Urgent, Right = Cancel + Submit */}
      <DialogActions
        sx={{
          p: 2.5,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          flexWrap: { xs: "wrap", md: "nowrap" }, // wraps on small screens
        }}
      >
        {/* Left side */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            flex: 1,
            minWidth: 0,
          }}
        >
          <TextField
            size="small"
            label="Initiator"
            value={form.initiator}
            disabled
            sx={{ minWidth: 220, maxWidth: 360, display:'none' }}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={form.urgent}
                onChange={handleChange("urgent")}
              />
            }
            label="Urgent"
          />
        </Box>

        {/* Right side */}
        <Box sx={{ display: "flex", gap: 1.5 }}>
          <Button
            variant="outlined"
            onClick={onClose}
            sx={{
              borderRadius: 4,
              textTransform: "none",
              color: "#000",
              borderColor: "#000",
              "&:hover": { borderColor: "#222" },
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={!canSubmit || loading}
            sx={{
              background: "#000",
              color: "#fff",
              borderRadius: 4,
              textTransform: "none",
              boxShadow: "none",
              "&:hover": { background: "rgb(0,128,0)" },
            }}
          >
            {loading ? "Submitting..." : "Submit"}
          </Button>
        </Box>
      </DialogActions>

    </Dialog></>
  );
}
